import { SetStateAction, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { formatDate } from '../../utils/validator';
import { CustomTable, ErrorModal, Popover, SearchBar, Spinner } from '../../components/shared';
import { allBookingsAPI } from '../../redux/features/bookings/bookingsSlice';
import { allBookingsColumnData, allBookingsTableColumnExtensions } from '../../utils/mockUpData';
import { useSearchParams } from 'react-router-dom';
import Filters from '../../components/shared/Filters';
import { TableActions } from '../../components/shared/StatusUtility';
import { Children } from './utils';

interface RestProps {
  row?: {
    status?: 'complete' | 'inprogress' | 'accepted' | 'pending' | 'declined';
  };
}

const Bookings = () => {
  const { loading, error, allBookings } = useAppSelector(state => state?.allBookings);
  const dispatch = useAppDispatch();

  // STATES
  const [activePopover, setActivePopover] = useState<number | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  // Toggle Popover
  const handleToggle = (index: number) => {
    setActivePopover(prev => (prev === index ? null : index));
  };

  // DATA PROVIDERS

  const dataProviders = [
    {
      columnName: ['action'],
      func: (restProps: any) => (
        <TableActions
          children={() => <Children restProps={restProps} recallList={handleBookingsSearch} />}
          restProps={restProps}
          isActive={activePopover === restProps?.row?.index}
          onToggle={handleToggle}
          height='max-content'
          ref={popoverRef}
        />
      )
    },
    {
      columnName: ['status'],
      func: (restProps: RestProps) => {
        const colors: { [key: string]: string } = {
          completed: 'border-success text-success',
          inprogress: 'border-blue-900 text-blue-900',
          accepted: 'border-primary text-primary',
          pending: 'border-goldEnrod text-goldEnrod',
          declined: 'border-red-500 text-red-500'
        };
        return (
          <div
            className={`border rounded w-min px-1 py-0.5 ${
              colors[restProps?.row?.status?.toLowerCase() || '']
            }`}
          >
            <p>{`${restProps?.row?.status?.charAt(0)?.toUpperCase()}${restProps?.row?.status
              ?.slice(1)
              ?.toLowerCase()}`}</p>
          </div>
        );
      }
    }
  ];

  const rowData = allBookings?.data?.map((item: any, index: number) => {
    return {
      index: index + 1,
      id: item?._id,
      bookingUID: item?.bookingUID || item?.bookingId,
      serviceName: item?.services?.map((_item: any) => _item.name).join(', '),
      customerName:
        `${item?.customer?.firstName ? item?.customer?.firstName : ''} ${
          item?.customer?.lastName ? item?.customer?.lastName : ''
        }`.trim() === ''
          ? 'N/A'
          : `${item?.customer?.firstName} ${item?.customer?.lastName}`,
      proName:
        `${item?.pro?.firstName ? item?.pro?.firstName : ''} ${
          item?.pro?.lastName ? item?.pro?.lastName : ''
        }`.trim() === ''
          ? 'N/A'
          : `${item?.pro?.firstName} ${item?.pro?.lastName}`,
      address: item?.address,
      bookingDate: formatDate(item?.bookingDate, 'MM/DD/YY, hh:mm A'),
      bookingDuration: item?.duration,
      status: item?.status,
      completeItem: item
    };
  });

  // States
  const [selectedIds, setSelectedIds] = useState([]);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [searchName, setSearchName] = useSearchParams({
    search: ''
  });

  const [filters, setFilters] = useState({
    Bookings: [
      {
        title: 'Past Bookings',
        value: false
      },
      {
        title: 'Upcoming Bookings',
        value: false
      }
    ]
  });
  // Get Query Pararms Value
  const searchedValue = searchName.get('search');

  // Handle Search
  const handleBookingsSearch = () => {
    if (searchedValue !== '') {
      dispatch(allBookingsAPI({ search: searchedValue }));
    } else {
      setSearchName();
      dispatch(allBookingsAPI({}));
    }
  };

  // Handle Filters
  const onChangeFilters = (title: string, item: any, index: number) => {
    if (title === 'clearAll') {
      setFilters({
        Bookings: filters.Bookings.map(b => ({ ...b, value: false }))
      });
      dispatch(
        allBookingsAPI({
          pastBookings: false,
          upcomingBookings: false
        })
      );
      setIsFiltersOpen(false);
      return;
    }
    if (title === 'Bookings') {
      setFilters(prevFilters => {
        const bookings = prevFilters.Bookings.map((b, i) => ({
          ...b,
          value: i === index ? !item?.value : false
        }));
        return { Bookings: bookings };
      });
      const pastBookings = index === 0 && item?.title === 'Past Bookings' ? !item?.value : false;
      const upcomingBookings =
        index === 1 && item?.title === 'Upcoming Bookings' ? !item?.value : false;
      dispatch(
        allBookingsAPI({
          pastBookings,
          upcomingBookings
        })
      );
    }
  };

  // UseEffect
  useEffect(() => {
    handleBookingsSearch();
    // eslint-disable-next-line
  }, [dispatch]);

  return (
    <>
      {loading ? (
        <Spinner />
      ) : (
        <div className='w-full h-full flex flex-col gap-2 p-3'>
          <div className='flex gap-2'>
            <SearchBar
              value={searchedValue || ''}
              onChange={e => {
                e.target.value =
                  e.target.value.charAt(0) === ' ' ? '' : e.target.value.replace(/ +$/, ' ');
                if (e.target.value === '') {
                  setSearchName();
                  dispatch(allBookingsAPI({}));
                } else {
                  setSearchName({
                    search: e.target.value
                  });
                }
              }}
              onSearch={() => handleBookingsSearch()}
              onClear={() => {
                setSearchName();
                dispatch(allBookingsAPI({}));
              }}
            />
            <div>
              <Popover
                menuHeading={'Filters'}
                dropdownOpen={isFiltersOpen}
                setDropdownOpen={setIsFiltersOpen}
                active={filters.Bookings[0].value || filters.Bookings[1].value || false}
              >
                <Filters options={filters} onChange={onChangeFilters} />
              </Popover>
            </div>
          </div>

          <ErrorModal showModal={Boolean(error)} errorMsg={error} />

          {/* Table */}
          <div className=''>
            <CustomTable
              rows={rowData?.length > 0 ? rowData : []}
              columns={allBookingsColumnData}
              tableColumnExtensions={allBookingsTableColumnExtensions}
              dataProviders={dataProviders}
              selectedIds={selectedIds}
              setSelectedIds={(selectedIds: SetStateAction<never[]>) => setSelectedIds(selectedIds)}
              componentHeader={true}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Bookings;
